import {
  MarketAccessory, MarketButton, MarketRow,
} from '@market/react';
import { useTranslation } from 'react-i18next';
import {
  MarketDualRotatingArrowsIcon, MarketWithdrawIcon,
} from '@squareup/market-react/icons';
import { withI18n } from '@squareup/service-i18n/components';
import { CreditLine_AutopaySettings_PaymentAmountType } from '@squareup/shared-types-protos/squareup/corporate_card/resources/credit_line';
import { RepaymentLimit } from '../repayment-limit-content/RepaymentLimitContent';
import styles from './RepaymentSummaryPage.module.css';

export interface SmartRepaymentConfig {
  readonly enabled: boolean;
  readonly portionOfSales: number;
  readonly selectedLimit: RepaymentLimit;
  readonly customLimitPercentage: number;
}

export interface AutopayDetails {
  readonly enabled: boolean;
  readonly paymentAmountType: CreditLine_AutopaySettings_PaymentAmountType;
  readonly nextPaymentDate: string;
  readonly paymentMethod: string;
}

export interface RepaymentSummaryPageProps {
  readonly autopayDetails: AutopayDetails;
  readonly smartRepayment: SmartRepaymentConfig;
  readonly onConfirm: () => void;
}

export const RepaymentSummaryPage = ({
  smartRepayment,
  autopayDetails,
  onConfirm,
}: RepaymentSummaryPageProps) => {
  const { t, i18n } = useTranslation();

  const isSmartRepaymentEnabled = smartRepayment.enabled;
  const isAutopayEnabled = autopayDetails.enabled;
  const nextPaymentDateObj = new Date(autopayDetails.nextPaymentDate);
  const paymentDay = nextPaymentDateObj.getUTCDate();
  const formattedNextPaymentDate = nextPaymentDateObj.toLocaleDateString(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

  let limitLabel: string;
  if (smartRepayment.selectedLimit === RepaymentLimit.MinimumPayment) {
    limitLabel = t('bankingShared.creditCard.repaymentSummary.limitMinimumPayment');
  } else if (smartRepayment.selectedLimit === RepaymentLimit.Custom) {
    limitLabel = t('bankingShared.creditCard.repaymentSummary.limitPercentageOfStatementBalance', { percentage: smartRepayment.customLimitPercentage });
  } else {
    limitLabel = t('bankingShared.creditCard.repaymentSummary.limitStatementBalance');
  }

  const isMinimumPayment = autopayDetails.paymentAmountType === CreditLine_AutopaySettings_PaymentAmountType.MIN_PAYMENT;
  let paymentAmountLabel: string;
  if (isMinimumPayment && isSmartRepaymentEnabled) {
    paymentAmountLabel = t('bankingShared.creditCard.repaymentSummary.paymentAmountRemainingMinimum');
  } else if (isMinimumPayment) {
    paymentAmountLabel = t('bankingShared.creditCard.repaymentSummary.paymentAmountMinimum');
  } else if (isSmartRepaymentEnabled) {
    paymentAmountLabel = t('bankingShared.creditCard.repaymentSummary.paymentAmountRemainingStatement');
  } else {
    paymentAmountLabel = t('bankingShared.creditCard.repaymentSummary.paymentAmountStatement');
  }

  return (
    <div className={styles.content}>
      <div>
        <h2 className="heading-30">
          {t('bankingShared.creditCard.repaymentSummary.title')}
        </h2>
        <p className="paragraph-30">
          {t('bankingShared.creditCard.repaymentSummary.subtitle')}
        </p>
      </div>

      <div>
        <MarketRow>
          <MarketAccessory slot="leading-accessory">
            <MarketWithdrawIcon />
          </MarketAccessory>
          <label className={`heading-20 ${styles.sectionLabel}`} slot="label">
            {t('bankingShared.creditCard.repaymentSummary.repayFromSales')}
          </label>
          <span
            className={`heading-20 ${styles.sectionLabel} ${isSmartRepaymentEnabled ? styles.statusBadgeOn : styles.statusBadgeOff}`}
            slot="side-label"
          >
            {t(isSmartRepaymentEnabled
              ? 'bankingShared.creditCard.repaymentSummary.on'
              : 'bankingShared.creditCard.repaymentSummary.off')}
          </span>
        </MarketRow>
        {isSmartRepaymentEnabled && (
          <>
            <MarketRow size="small">
              <label slot="label">
                {t('bankingShared.creditCard.repaymentSummary.percentageOfCardSales')}
              </label>
              <span slot="side-label">{smartRepayment.portionOfSales}%</span>
            </MarketRow>
            <MarketRow size="small">
              <label slot="label">
                {t('bankingShared.creditCard.repaymentSummary.limit')}
              </label>
              <span slot="side-label">{limitLabel}</span>
            </MarketRow>
          </>
        )}
      </div>

      <div>
        <MarketRow>
          <MarketAccessory slot="leading-accessory">
            <MarketDualRotatingArrowsIcon />
          </MarketAccessory>
          <label className={`heading-20 ${styles.sectionLabel}`} slot="label">
            {t('bankingShared.creditCard.repaymentSummary.autopay')}
          </label>
          <span
            className={`heading-20 ${styles.sectionLabel} ${isAutopayEnabled ? styles.statusBadgeOn : styles.statusBadgeOff}`}
            slot="side-label"
          >
            {t(isAutopayEnabled
              ? 'bankingShared.creditCard.repaymentSummary.on'
              : 'bankingShared.creditCard.repaymentSummary.off')}
          </span>
        </MarketRow>
        {isAutopayEnabled && (
          <>
            <MarketRow size="small">
              <label slot="label">
                {t('bankingShared.creditCard.repaymentSummary.paymentAmount')}
              </label>
              <span slot="side-label">{paymentAmountLabel}</span>
            </MarketRow>
            <MarketRow size="small">
              <label slot="label">
                {t('bankingShared.creditCard.repaymentSummary.paymentSchedule')}
              </label>
              <span slot="side-label">
                {t('bankingShared.creditCard.repaymentSummary.paymentEachMonth', { day: paymentDay })}
              </span>
            </MarketRow>
            <MarketRow size="small">
              <label slot="label">
                {t('bankingShared.creditCard.repaymentSummary.nextPaymentDate')}
              </label>
              <span slot="side-label">{formattedNextPaymentDate}</span>
            </MarketRow>
            <MarketRow size="small">
              <label slot="label">
                {t('bankingShared.creditCard.repaymentSummary.paymentMethod')}
              </label>
              <span slot="side-label">{autopayDetails.paymentMethod}</span>
            </MarketRow>
          </>
        )}
      </div>

      <MarketButton
        rank="primary"
        onClick={onConfirm}
      >
        {t('bankingShared.creditCard.repaymentSummary.confirm')}
      </MarketButton>
    </div>
  );
};

export const RepaymentSummaryPageI18n = withI18n(RepaymentSummaryPage);
